import { ITheme, IStyle, mergeStyleSets, getTheme } from '@fluentui/react';
import React, { useMemo } from 'react';
import { IProjectConfiguration } from '../../../../entities-generated/models/project/project-configuration';
import { BwjCard } from '../../../../framework/components/bwj-layout';
import { ProjectService } from '../../../services/project';
import { CartInstallationSiteListing } from '../../../cart/components/cart-install-site-listing';
import { any, where } from '../../../../framework/utils/collection';
import { formatString } from '../../../../framework/utils/string';
import * as CheckoutEvents from '../../events';

interface ICheckoutSummaryProps {
  projectConfig: IProjectConfiguration;
}

const getStyleClasses = (theme: ITheme) => {
  const checkoutSummary: IStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    flexWrap: 'nowrap',
    gap: '0.75em',
    width: '100%',
    listStyleType: 'none',
    margin: 0,
    marginTop: '2.5rem',
    padding: 0,
  };

  const checkoutSummaryLineItem: IStyle = {
    display: 'block',
  };

  const checkoutSummaryLineItemContent: IStyle = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',

    '& > label': {
      fontWeight: 'bold',
    },
  };

  const checkoutSummarySubtotal: IStyle = {
    ...checkoutSummaryLineItem,
    borderTop: `1px solid ${theme.palette.neutralTertiary}`,
    paddingTop: '0.25em',
  };

  const checkoutSummaryBottomLine: IStyle = {
    ...checkoutSummaryLineItem,
    borderTop: `2px solid ${theme.palette.neutralPrimary}`,
    paddingTop: '0.25em',
  };

  return mergeStyleSets({
    checkoutSummary,
    checkoutSummaryLineItem,
    checkoutSummaryLineItemContent,
    checkoutSummarySubtotal,
    checkoutSummaryBottomLine,
  });
};

export const CheckoutSummary: React.FunctionComponent<ICheckoutSummaryProps> = (props) => {
  const theme = getTheme();
  const cs = getStyleClasses(theme);
  const { projectConfig } = props;
  const allowExclusion = useMemo(() => ProjectService.getActiveInstallationCount(projectConfig) > 1, [projectConfig]);
  const displayableLocations = useMemo(() => {
    return where(projectConfig.installLocations, x => any(x.doorInstallations, d => d.isExcluded === false));
  }, [projectConfig]);

  return (
    <BwjCard>
      {displayableLocations.map(location =>
        <CartInstallationSiteListing
          key={location.name}
          site={location}
          excludeExcluded={true}
          suppressNotification={true}
          allowExclusion={allowExclusion}
          onExclude={(door, site) => CheckoutEvents.fireExcludeInstallation(door, site)}
          onInclude={() => {}}
          onDoorSelected={(door, site) => CheckoutEvents.fireDoorSelected(door, site)}
          onHandleSelected={(door, site) => CheckoutEvents.fireHandleSelected(door, site)}
        />
      )}
      <ul className={cs.checkoutSummary}>
        <li className={cs.checkoutSummarySubtotal}>
          <div className={cs.checkoutSummaryLineItemContent}>
            <label>{'Subtotal'}</label>
            <span>{formatString('{value:c#.2-USD}', { value: projectConfig.subtotal / 100 }).formattedString}</span>
          </div>
        </li>
        <li className={cs.checkoutSummaryLineItem}>
          <div className={cs.checkoutSummaryLineItemContent}>
            <label>{'Sales tax'}</label>
            <span>{formatString('{value:c#.2-USD}', { value: projectConfig.tax / 100 }).formattedString}</span>
          </div>
        </li>
        <li className={cs.checkoutSummaryBottomLine}>
          <div className={cs.checkoutSummaryLineItemContent}>
            <label>{'Total'}</label>
            <span>{formatString('{value:c#.2-USD}', { value: projectConfig.total / 100 }).formattedString}</span>
          </div>
        </li>
      </ul>
    </BwjCard>
  );
};
